

import { IntelligenceTier, IntelligenceTierEnum, NovelLanguage, NovelLanguageEnum } from '@common/schemas/novel.schema';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DeleteConfirmationModal } from '@/components/mui-delete-confirmation-modal';
import { CollaborationSettingsSection } from './collaboration-settings-section';
import { ImageStyleSection } from './image-style-section';

import type { Novel } from '@/lib/types';

import { useNovel } from '@/context/novel-context';
import { useFirebase } from '@/firebase';
import { useExampleNovel } from '@/hooks/use-example-novel';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { useSubscription } from '@/hooks/useSubscription';
import { getThemeDefaultCategories } from '@/lib/theme-utils';

export function NovelSettingsSection() {
  const { novel } = useNovel();
  if (!novel.id) return null;

  const { t } = useTranslation();
  const { backend } = useFirebase();
  const { setSelectedNovelId, updateNovelItem } = useNovelRepository();
  const { hasFeature } = useSubscription();
  const { isExampleNovel } = useNovel();
  const { dismissExampleNovel } = useExampleNovel();

  const canCreateAIImages = hasFeature('can_create_ai_images');
  const isGenerativeAiAllowed = hasFeature('is_generative_ai_allowed');

  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(novel.title);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSavingLanguage, setIsSavingLanguage] = useState(false);
  const [isSavingTier, setIsSavingTier] = useState(false);
  const [isUpdatingField, setIsUpdatingField] = useState<string | null>(null);

  const CLOSED_THEMES = ['fantasy', 'scifi', 'police', 'horror', 'drama', 'romance', 'dark'];
  const currentTheme = novel.theme || '';
  const isThemeClosed = CLOSED_THEMES.includes(currentTheme);

  const [selectedThemeKey, setSelectedThemeKey] = useState(isThemeClosed ? currentTheme : (currentTheme ? 'custom' : ''));
  const [customThemeValue, setCustomThemeValue] = useState(isThemeClosed ? '' : currentTheme);

  useEffect(() => {
    setEditedTitle(novel.title);
  }, [novel.title]);

  useEffect(() => {
    if (novel.theme) {
      const isThemeClosedNow = CLOSED_THEMES.includes(novel.theme);
      setSelectedThemeKey(isThemeClosedNow ? novel.theme : 'custom');
      if (!isThemeClosedNow) {
        setCustomThemeValue(novel.theme);
      }
    } else {
      setSelectedThemeKey('');
      setCustomThemeValue('');
    }
  }, [novel.theme]);

  const handleUpdateField = async (fieldName: 'theme' | 'audience' | 'worldSetting', value: string) => {
    if (!backend || novel[fieldName] === value) return;
    setIsUpdatingField(fieldName);
    try {
      const updatePayload: Partial<Novel> = {
        [fieldName]: value,
      } as Partial<Novel>;

      if (fieldName === 'theme') {
        const defaultCategories = getThemeDefaultCategories(value);
        if (Object.keys(defaultCategories).length > 0) {
          const currentCategories = novel.entitiesMeta?.codexCategories || {};
          const newCategories = { ...currentCategories };
          const currentSettings = novel.entitiesMeta?.settings || {};
          const newSettings = { ...currentSettings };
          let hasNew = false;

          Object.entries(defaultCategories).forEach(([categoryId, categoryName]) => {
            if (!newCategories[categoryId]) {
              newCategories[categoryId] = categoryName;
              newSettings[`codex:${categoryId}`] = [];
              hasNew = true;
            }
          });

          if (hasNew) {
            updatePayload.entitiesMeta = {
              ...novel.entitiesMeta,
              codexCategories: newCategories,
              settings: newSettings,
            };
          }
        }
      }

      if (backend) {
        await backend.novels.updateNovel(novel.ownerId, novel.id, updatePayload);
      }
    } catch (error) {
      console.error(`Error updating novel ${fieldName}:`, error);
    } finally {
      setIsUpdatingField(null);
    }
  };

  const handleSaveLanguage = async (newLanguage: NovelLanguage) => {
    if (!backend || newLanguage === novel.language) return;
    setIsSavingLanguage(true);
    try {
      await backend.novels.updateNovel(novel.ownerId, novel.id, {
        language: newLanguage,
      });
    } catch (error) {
      console.error('Error updating novel language:', error);
    } finally {
      setIsSavingLanguage(false);
    }
  };

  const handleSaveTier = async (newTier: IntelligenceTier) => {
    if (newTier === novel.aiSettings?.intelligenceTier) return;
    setIsSavingTier(true);
    try {
      await updateNovelItem({
        aiSettings: {
          ...novel.aiSettings,
          intelligenceTier: newTier,
        },
      });
    } catch (error) {
      console.error('Error updating novel intelligence tier:', error);
    } finally {
      setIsSavingTier(false);
    }
  };

  const handleSaveTitle = async () => {
    if (!backend || !editedTitle.trim() || editedTitle === novel.title) {
      setIsEditing(false);
      setEditedTitle(novel.title);
      return;
    }

    setIsSaving(true);
    try {
      await backend.novels.updateNovel(novel.ownerId, novel.id, {
        title: editedTitle.trim(),
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating novel title:', error);
      setEditedTitle(novel.title);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditedTitle(novel.title);
    setIsEditing(false);
  };

  const handleDeleteNovel = async () => {
    // Example novels cannot be deleted — dismissal just hides them for this user
    if (isExampleNovel) {
      dismissExampleNovel();
      setSelectedNovelId(null);
      setDeleteDialogOpen(false);
      return;
    }

    if (!backend) return;

    setIsDeleting(true);
    try {
      await backend.novels.deleteNovel(novel.ownerId, novel.id);
      setSelectedNovelId(null);
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error('Error deleting novel:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Accordion
        sx={{
          mt: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            borderRadius: 3,
            '& .MuiAccordionSummary-content': { my: 1.5 },
          }}
        >
          <Typography variant="h3" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
            {t('dashboard.settings.title')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={3}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 4 }}>
                {/* Language Settings */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('dashboard.settings.generation_language')}
                  </Typography>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={novel.language || 'English'}
                      onChange={(e) => handleSaveLanguage(e.target.value as NovelLanguage)}
                      disabled={isSavingLanguage}
                    >
                      {NovelLanguageEnum.options.map(option => (
                        <MenuItem key={option} value={option}>
                          {t(`language.${option.toLowerCase()}`)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Grid>

              {isGenerativeAiAllowed && (
                <Grid size={{ xs: 12, md: 4 }}>
                  {/* Intelligence Tier Settings */}
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      {t('dashboard.settings.intelligence_tier')}
                    </Typography>
                    <FormControl size="small" fullWidth>
                      <Select
                        value={novel.aiSettings?.intelligenceTier || 'eco'}
                        onChange={(e) => handleSaveTier(e.target.value as IntelligenceTier)}
                        disabled={isSavingTier}
                      >
                        {IntelligenceTierEnum.options.map(tier => (
                          <MenuItem key={tier} value={tier}>
                            {t(`dashboard.settings.intelligence_tiers.${tier}`)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Grid>
              )}

              <Grid size={{ xs: 12, md: 4 }}>
                {/* Title Edit */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('dashboard.settings.novel_title')}
                  </Typography>
                  {isEditing ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        fullWidth
                        value={editedTitle}
                        onChange={(e) => setEditedTitle(e.target.value)}
                        size="small"
                        disabled={isSaving}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveTitle();
                          }
                        }}
                      />
                      <IconButton
                        color="primary"
                        onClick={handleSaveTitle}
                        disabled={isSaving || !editedTitle.trim()}
                        size="small"
                      >
                        <SaveIcon />
                      </IconButton>
                      <IconButton
                        color="default"
                        onClick={handleCancelEdit}
                        disabled={isSaving}
                        size="small"
                      >
                        <CloseIcon />
                      </IconButton>
                    </Stack>
                  ) : (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body1" sx={{ flex: 1 }}>
                        {novel.title}
                      </Typography>
                      <IconButton color="primary" onClick={() => setIsEditing(true)} size="small">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  )}
                </Box>
              </Grid>
            </Grid>

            {/* Theme, Audience, World Setting row */}
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('novel.theme')}
                  </Typography>
                  <FormControl size="small" fullWidth disabled={isUpdatingField === 'theme'}>
                    <Select
                      value={selectedThemeKey}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSelectedThemeKey(val);
                        if (val !== 'custom') {
                          handleUpdateField('theme', val);
                          setCustomThemeValue('');
                        } else {
                          setCustomThemeValue('');
                        }
                      }}
                    >
                      <MenuItem value="fantasy">{t('novel.themes.fantasy')}</MenuItem>
                      <MenuItem value="scifi">{t('novel.themes.scifi')}</MenuItem>
                      <MenuItem value="police">{t('novel.themes.police')}</MenuItem>
                      <MenuItem value="horror">{t('novel.themes.horror')}</MenuItem>
                      <MenuItem value="drama">{t('novel.themes.drama')}</MenuItem>
                      <MenuItem value="romance">{t('novel.themes.romance')}</MenuItem>
                      <MenuItem value="dark">{t('novel.themes.dark')}</MenuItem>
                      <MenuItem value="custom">{t('novel.themes.custom')}</MenuItem>
                    </Select>
                  </FormControl>

                  {selectedThemeKey === 'custom' && (
                    <Box sx={{ mt: 2 }}>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder={t('novel.themes.custom_placeholder')}
                        value={customThemeValue}
                        onChange={(e) => {
                          setCustomThemeValue(e.target.value);
                        }}
                        onBlur={() => {
                          if (customThemeValue.trim()) {
                            handleUpdateField('theme', customThemeValue.trim());
                          }
                        }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && customThemeValue.trim()) {
                            handleUpdateField('theme', customThemeValue.trim());
                          }
                        }}
                        disabled={isUpdatingField === 'theme'}
                      />
                    </Box>
                  )}
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('novel.audience')}
                  </Typography>
                  <Autocomplete
                    freeSolo
                    size="small"
                    options={[
                      t('novel.audiences.adult'),
                      t('novel.audiences.young_adult'),
                      t('novel.audiences.children')
                    ]}
                    value={novel.audience || ''}
                    onInputChange={(_, newValue) => handleUpdateField('audience', newValue)}
                    disabled={isUpdatingField === 'audience'}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth />
                    )}
                  />
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('novel.world_setting')}
                  </Typography>
                  <Autocomplete
                    freeSolo
                    size="small"
                    options={[
                      t('novel.world_settings.fantasy'),
                      t('novel.world_settings.ancient_world'),
                      t('novel.world_settings.victorian_era'),
                      t('novel.world_settings.sci_fi')
                    ]}
                    value={novel.worldSetting || ''}
                    onInputChange={(_, newValue) => handleUpdateField('worldSetting', newValue)}
                    disabled={isUpdatingField === 'worldSetting'}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth />
                    )}
                  />
                </Box>
              </Grid>
            </Grid>

            {/* Image Style Settings - Only for tier 3+ */}
            {canCreateAIImages && (
              <Box>
                <ImageStyleSection />
              </Box>
            )}

            {/* Delete / Hide Novel */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                {t('dashboard.settings.danger_zone')}
              </Typography>
              <Button
                variant="outlined"
                color={isExampleNovel ? 'warning' : 'error'}
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
                sx={{ borderRadius: 1.5 }}
              >
                {isExampleNovel
                  ? t('example_novel.hide')
                  : t('dashboard.settings.delete_button')}
              </Button>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                {isExampleNovel
                  ? t('example_novel.readonly_hint')
                  : t('dashboard.settings.delete_warning')}
              </Typography>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <CollaborationSettingsSection />

      <DeleteConfirmationModal
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteNovel}
        title={
          isExampleNovel
            ? t('example_novel.hide')
            : t('dashboard.settings.dialog_title')
        }
        description={
          isExampleNovel
            ? t('example_novel.readonly_hint')
            : t('dashboard.settings.dialog_desc', { title: novel.title })
        }
        isDeleting={isDeleting}
      />
    </>
  );
}
